import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useArtists, useArtist, useReleases, useArtistSuggestions } from '../hooks'
import { ArtistSuggestion } from '../api/artists'
import { ArtistFilters, ArtistSortField, Artist } from '../types/artist'
import { ArtistHeader } from '../components/artists/ArtistHeader'
import { ErrorBoundary } from '../components/common/ErrorBoundary'
import { ReleaseFilters } from '../types/release'
import { useViewSort, mapSortForEntity } from '../context/ViewSortContext'
import { LoadingState } from '../components/common/Spinner'
import { Pagination } from '../components/common/Pagination'
import { ViewSortControls } from '../components/common/ViewSortControls'
import { IconButton, SearchInput, Button, Badge } from '../components/common'
import { ArtistGrid } from '../components/artists/ArtistGrid'
import { ArtistFilters as ArtistFiltersComponent } from '../components/artists/ArtistFilters'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import { cn } from '../utils'
import styles from './PageStyles.module.css'
import artistStyles from '../components/artists/Artists.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

interface ArtistTab {
  id: number
  name: string
  artistId: number
}

export function ArtistsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()

  const [filters, setFilters] = useState<ArtistFilters>(() => {
    try {
      const saved = sessionStorage.getItem('stonks_artists_filters')
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return { page: 1, per_page: 50, sort: mapSortForEntity(globalSort) as ArtistSortField, order: globalOrder }
  })
  const [search, setSearch] = useState(() => {
    try { const s = sessionStorage.getItem('stonks_artists_filters'); if (s) { return JSON.parse(s).q || '' } } catch {} return ''
  })
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [openTabs, setOpenTabs] = useState<ArtistTab[]>(() => {
    try {
      const saved = localStorage.getItem('stonks_artist_tabs')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [activeTabId, setActiveTabId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem('stonks_artist_active_tab')
      return saved ? parseInt(saved) : null
    } catch {
      return null
    }
  })
  const [showFilters, setShowFilters] = useState(false)
  const [releasePage, setReleasePage] = useState(1)
  const [artistSubTab, setArtistSubTab] = useState<'releases' | 'appearances'>('releases')
  const [releaseSearch, setReleaseSearch] = useState('')

  // Open artist tab from URL params (e.g. from player link)
  useEffect(() => {
    const artistIdParam = searchParams.get('artist_id')
    const artistName = searchParams.get('artist_name')
    if (!artistIdParam || !artistName) return
    const artistId = parseInt(artistIdParam, 10)
    if (isNaN(artistId)) return
    setOpenTabs(prev => {
      const existing = prev.find(t => t.artistId === artistId)
      if (existing) {
        setActiveTabId(existing.id)
        return prev
      }
      const newTab = { id: Date.now(), name: artistName, artistId }
      setActiveTabId(newTab.id)
      return [...prev, newTab]
    })
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // Persist filters to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem('stonks_artists_filters', JSON.stringify(filters)) } catch {}
  }, [filters])

  // Sync global sort changes to artist list filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      sort: mapSortForEntity(globalSort) as ArtistSortField,
      order: globalOrder,
      page: 1,
    }))
  }, [globalSort, globalOrder])

  const { data, isLoading, error } = useArtists(filters)
  const { data: suggestions = [] } = useArtistSuggestions(search)

  // Get releases for active tab using global sort
  const activeTab = openTabs.find(t => t.id === activeTabId)
  const { data: artistDetail } = useArtist(activeTab?.artistId ?? 0)
  const releaseFilters: ReleaseFilters = {
    artist_id: activeTab?.artistId,
    page: releasePage,
    per_page: 50,
    sort: globalSort,
    order: globalOrder,
    ...(releaseSearch ? { q: releaseSearch } : {}),
    ...(artistSubTab === 'appearances' ? { appearances: true } : {}),
  }

  // Save tabs to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('stonks_artist_tabs', JSON.stringify(openTabs))
    } catch { /* ignore */ }
  }, [openTabs])

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    try {
      if (activeTabId !== null) {
        localStorage.setItem('stonks_artist_active_tab', activeTabId.toString())
      } else {
        localStorage.removeItem('stonks_artist_active_tab')
      }
    } catch { /* ignore */ }
  }, [activeTabId])

  const { data: releasesData, isLoading: isLoadingReleases } = useReleases(
    releaseFilters,
    { enabled: activeTabId !== null && !!activeTab?.artistId }
  )

  // Accept the value from SearchField's onSubmit so clearing (which fires
  // onSubmit('')) doesn't re-apply the stale `search` closure — that bug left the
  // query stuck and re-persisted it to sessionStorage, so it kept coming back.
  const handleSearch = useCallback((value?: string) => {
    setShowSuggestions(false)
    const q = value !== undefined ? value : search
    setFilters(prev => ({ ...prev, q: q || undefined, page: 1 }))
  }, [search])

  const handleClearSearch = () => {
    setSearch('')
    setShowSuggestions(false)
    setFilters(prev => ({ ...prev, q: undefined, page: 1 }))
  }

  const handleSuggestionClick = (suggestion: ArtistSuggestion) => {
    setShowSuggestions(false)
    setSearch(suggestion.name)
    handleArtistClick({ id: suggestion.id, name: suggestion.name } as any)
  }

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleArtistClick = (artist: Artist) => {
    const existingTab = openTabs.find(t => t.artistId === artist.id)
    if (existingTab) {
      setActiveTabId(existingTab.id)
    } else {
      const newTab: ArtistTab = {
        id: Date.now(),
        name: artist.name,
        artistId: artist.id,
      }
      setOpenTabs(prev => [...prev, newTab])
      setActiveTabId(newTab.id)
    }
    setArtistSubTab('releases')
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: number) => {
    e.stopPropagation()
    setOpenTabs(prev => prev.filter(t => t.id !== tabId))
    if (activeTabId === tabId) {
      setActiveTabId(null)
      setReleasePage(1)
    }
  }

  const handleTabClick = (tabId: number | null) => {
    setActiveTabId(tabId)
    setReleasePage(1)
    setArtistSubTab('releases')
    setReleaseSearch('')
  }

  if (error) {
    return (
      <div className={styles.error}>
        <i className="bi bi-exclamation-triangle"></i>
        <p>Failed to load artists</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1>
            Artists
          </h1>
        </div>
      </div>

      {/* Tab bar — always visible (even with no open tabs) for stable layout */}
      <div className={releaseStyles.tabBar}>
        <button
          className={cn(releaseStyles.tab, activeTabId === null && releaseStyles.tabActive)}
          onClick={() => handleTabClick(null)}
        >
          All Artists
        </button>
        {openTabs.map(tab => (
          <button
            key={tab.id}
            className={cn(releaseStyles.tab, activeTabId === tab.id && releaseStyles.tabActive)}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.name}
            <span className={releaseStyles.tabClose} onClick={(e) => handleCloseTab(e, tab.id)}>
              <i className="bi bi-x"></i>
            </span>
          </button>
        ))}
      </div>

      {/* Search + sort toolbar — list view only */}
      {activeTabId === null && (
        <>
          <div className={releaseStyles.searchFilterRow}>
            <div className={artistStyles.searchBarWrap}>
              <SearchInput
                value={search}
                onChange={v => { setSearch(v); setShowSuggestions(true) }}
                onSubmit={handleSearch}
                onClear={handleClearSearch}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={e => { if (e.key === 'Escape') setShowSuggestions(false) }}
                placeholder="Search artists..."
                block
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className={artistStyles.suggestions}>
                  {suggestions.map(s => (
                    <button
                      key={s.id}
                      className={artistStyles.suggestionItem}
                      onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s) }}
                    >
                      <span className={artistStyles.suggestionName}>{s.name}</span>
                      {s.matched_alias && (
                        <span className={artistStyles.suggestionAlias}>via {s.matched_alias}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {(() => {
              const count = [
                filters.min_elevated_score, filters.min_match_score,
                filters.min_release_count, filters.min_want,
                filters.year_from, filters.year_to,
              ].filter(v => v != null).length
                + (filters.genres?.length ? 1 : 0)
                + (filters.styles?.length ? 1 : 0)
                + (filters.countries?.length ? 1 : 0)
              return (
                <Button
                  variant="secondary"
                  active={showFilters}
                  icon={showFilters ? 'bi bi-funnel-fill' : 'bi bi-funnel'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                  {count > 0 && <Badge count>{count}</Badge>}
                </Button>
              )
            })()}
            {filters.genres?.map(g => (
              <span key={`g-${g}`} className={releaseStyles.filterChip}>
                {g}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${g} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, genres: prev.genres?.filter(x => x !== g), page: 1 }))} />
              </span>
            ))}
            {filters.styles?.map(s => (
              <span key={`s-${s}`} className={releaseStyles.filterChip}>
                {s}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${s} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, styles: prev.styles?.filter(x => x !== s), page: 1 }))} />
              </span>
            ))}
            {filters.countries?.map(c => (
              <span key={`c-${c}`} className={releaseStyles.filterChip}>
                {c}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${c} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, countries: prev.countries?.filter(x => x !== c), page: 1 }))} />
              </span>
            ))}
            <div className={styles.controlsTrailing}>
              <ViewSortControls hideViewToggle />
            </div>
          </div>
          {showFilters && (
            <ArtistFiltersComponent filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} />
          )}
        </>
      )}

      {/* Content */}
      {activeTabId === null ? (
        <div className={styles.gridView}>
          {isLoading ? (
            <LoadingState message="Loading artists..." />
          ) : (
            <>
              {data?.artists && data.artists.length > 0 ? (
                <ArtistGrid artists={data.artists} onArtistClick={handleArtistClick} />
              ) : (
                <div className={styles.empty}>
                  <i className="bi bi-person-circle"></i>
                  <p>No artists found</p>
                  {filters.q && (
                    <p className={styles.emptyHint}>Try adjusting your search</p>
                  )}
                </div>
              )}

              {data?.pagination && (
                <Pagination
                  page={data.pagination.page}
                  totalPages={data.pagination.pages}
                  hasNext={data.pagination.page < data.pagination.pages}
                  hasPrev={data.pagination.page > 1}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <div className={styles.scrollContent}>
          {artistDetail?.artist && (
            <ErrorBoundary fallback={null}>
              <ArtistHeader artist={artistDetail.artist} />
            </ErrorBoundary>
          )}

          {/* Search + sort toolbar — below artist header */}
          <div className={releaseStyles.searchFilterRow}>
            <div className={releaseStyles.searchBarStandalone}>
              <i className="bi bi-search" />
              <input
                type="text"
                placeholder="Search releases..."
                value={releaseSearch}
                onChange={e => { setReleaseSearch(e.target.value); setReleasePage(1) }}
              />
              {releaseSearch && (
                <IconButton variant="ghost" size="sm" icon="bi bi-x-lg" aria-label="Clear search" onClick={() => { setReleaseSearch(''); setReleasePage(1) }} />
              )}
            </div>
            <div className={styles.controlsTrailing}>
              <ViewSortControls />
            </div>
          </div>

          {/* Releases / Appearances sub-tabs */}
          <div className={releaseStyles.tabBar}>
            <button
              className={cn(releaseStyles.tab, artistSubTab === 'releases' && releaseStyles.tabActive)}
              onClick={() => { setArtistSubTab('releases'); setReleasePage(1) }}
            >
              <i className="bi bi-vinyl"></i> Releases
            </button>
            <button
              className={cn(releaseStyles.tab, artistSubTab === 'appearances' && releaseStyles.tabActive)}
              onClick={() => { setArtistSubTab('appearances'); setReleasePage(1) }}
            >
              <i className="bi bi-person-badge"></i> Appearances
            </button>
          </div>

          {isLoadingReleases ? (
            <LoadingState message={`Loading ${artistSubTab}...`} />
          ) : (
            <>
              <ReleaseListView
                releases={releasesData?.releases ?? []}
                viewMode={viewMode}
                emptyMessage={artistSubTab === 'appearances' ? 'No appearances found for this artist' : 'No releases found for this artist'}
              />
              {releasesData?.pagination && (
                <Pagination
                  page={releasePage}
                  totalPages={releasesData.pagination.total_pages}
                  hasNext={releasePage < releasesData.pagination.total_pages}
                  hasPrev={releasePage > 1}
                  onPageChange={(p) => setReleasePage(p)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
